import { useState, useEffect } from "react";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { Outlet, useLocation } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Search, Monitor, Keyboard, HelpCircle } from "lucide-react";
import { NotificationBell } from "@/components/NotificationBell";
import { OfflineIndicator } from "@/components/OfflineIndicator";
import { useAuth } from "@/contexts/AuthContext";
import { useCompany } from "@/contexts/CompanyContext";
import { usePageViewTracker } from "@/hooks/useAnalytics";
import { useDesktopMode } from "@/contexts/DesktopModeContext";
import { useEasyMode } from "@/contexts/EasyModeContext";
import { EasyModeOverlay } from "@/components/EasyModeOverlay";
import { EasyModeKeyboardHints } from "@/components/EasyModeKeyboardHints";
import { useGlobalShortcuts } from "@/hooks/useGlobalShortcuts";
import { ShortcutsHelpDialog } from "@/components/ShortcutsHelpDialog";
import { CommandPalette } from "@/components/CommandPalette";
import { Button } from "@/components/ui/button";
import DesktopModeLayout from "@/components/DesktopModeLayout";

const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const { activeCompany } = useCompany();
  const location = useLocation();
  const { isDesktopMode, enableDesktopMode } = useDesktopMode();
  const { isEasyMode, toggleEasyMode } = useEasyMode();
  const { showHelp, setShowHelp } = useGlobalShortcuts();
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const shouldReduceMotion = useReducedMotion();
  usePageViewTracker();

  const isOnboarding = location.pathname === "/dashboard/onboarding";
  const needsOnboarding = !activeCompany || !activeCompany.onboardingCompleted;

  // Temporary debug logs for tracing dashboard bootstrap
  useEffect(() => {
    console.debug("[dashboard] boot", {
      userId: user?.id,
      role: user?.role,
      company: user?.company ?? null,
    });
  }, [user?.id, user?.role, user?.company]);

  // Ctrl+K to open command palette
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setShowCommandPalette(v => !v);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  if (isDesktopMode) {
    return <DesktopModeLayout />;
  }

  // Distraction-free full-width layout for first-time company onboarding
  if (isOnboarding && needsOnboarding) {
    return (
      <div className="min-h-screen min-h-[100dvh] flex flex-col bg-background">
        <header className="h-14 flex items-center justify-between border-b border-slate-200 bg-white px-4 sm:px-8 sticky top-0 z-40">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center text-white font-bold font-display shadow-2xs">
              A
            </div>
            <div>
              <span className="font-bold font-display text-slate-900 tracking-tight text-sm sm:text-base">AccouPro</span>
              <span className="text-[11px] text-slate-500 ml-2 hidden sm:inline">First-Time Setup</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-xs font-semibold text-foreground">{user?.name || user?.email}</p>
              <p className="text-[10px] text-muted-foreground">{user?.email}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={logout} className="text-xs text-muted-foreground hover:text-foreground">
              Logout
            </Button>
          </div>
        </header>
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen min-h-[100dvh] flex w-full bg-background">
        <AppSidebar />
        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-14 flex items-center justify-between border-b border-slate-200 bg-white px-3 sm:px-5 sticky top-0 z-40 safe-top">
            <div className="flex items-center gap-2 sm:gap-4">
              <SidebarTrigger className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg p-1.5 transition-colors touch-target flex items-center justify-center" />
              <button
                onClick={() => setShowCommandPalette(true)}
                className="hidden md:flex items-center gap-2.5 bg-slate-50 hover:bg-slate-100/80 rounded-lg px-3.5 py-1.5 border border-slate-200 text-slate-500 transition-colors cursor-pointer"
              >
                <Search className="h-4 w-4 text-slate-400" />
                <span className="text-xs text-slate-500 w-36 text-left">Search anything...</span>
                <kbd className="px-1.5 py-0.5 rounded bg-slate-200/80 border border-slate-300/60 font-mono text-[10px] text-slate-600 font-semibold">Ctrl+K</kbd>
              </button>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowHelp(true)}
                className="hidden md:flex h-8 gap-1.5 text-xs text-slate-600 hover:text-slate-900 border-slate-200 hover:bg-slate-100 rounded-lg"
                title="Keyboard Shortcuts (F1)"
              >
                <HelpCircle className="h-3.5 w-3.5 text-slate-500" />
                <span>Help</span>
                <kbd className="px-1 py-0.5 rounded bg-slate-200/80 border border-slate-300/60 font-mono text-[9px] text-slate-600 font-semibold">F1</kbd>
              </Button>
              <Button
                size="sm"
                variant={isEasyMode ? "default" : "outline"}
                onClick={toggleEasyMode}
                className={`hidden md:flex h-8 gap-1.5 text-xs rounded-lg transition-colors ${
                  isEasyMode 
                    ? "bg-blue-600 hover:bg-blue-700 text-white border-blue-600 shadow-2xs font-medium" 
                    : "border-slate-200 text-slate-700 hover:bg-slate-100"
                }`}
              >
                <Keyboard className="h-3.5 w-3.5" />
                {isEasyMode ? "Easy Mode ON" : "Easy Mode"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={enableDesktopMode}
                className="hidden md:flex h-8 gap-1.5 text-xs text-slate-700 hover:bg-slate-100 border-slate-200 rounded-lg"
              >
                <Monitor className="h-3.5 w-3.5 text-slate-500" />
                Desktop Mode
              </Button>
              <OfflineIndicator />
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={logout}
                  className="text-slate-600 hover:text-rose-600 hover:bg-rose-50 text-xs rounded-lg px-2.5 h-8 transition-colors"
                >
                  Logout
                </Button>
                <div className="w-8 h-8 sm:w-8 sm:h-8 rounded-lg bg-blue-50 flex items-center justify-center border border-blue-200 shadow-2xs">
                  <span className="text-[11px] font-bold text-blue-700">
                    {user?.name?.split(" ").map(n => n[0]).join("") || "U"}
                  </span>
                </div>
              </div>
              <NotificationBell />
            </div>
          </header>
          <main className={`flex-1 p-3 sm:p-4 md:p-6 overflow-auto safe-bottom relative ${isEasyMode ? "pb-14" : ""}`}>
            <AnimatePresence mode="wait">
              <motion.div
                key={location.pathname}
                initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: -4 }}
                transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                className="min-h-full w-full"
              >
                <Outlet />
              </motion.div>
            </AnimatePresence>
          </main>
        </div>
      </div>
      <EasyModeOverlay />
      <EasyModeKeyboardHints />
      <ShortcutsHelpDialog open={showHelp} onClose={() => setShowHelp(false)} />
      <CommandPalette open={showCommandPalette} onOpenChange={setShowCommandPalette} />
    </SidebarProvider>
  );
};

export default DashboardLayout;
